import React from 'react';
import { Document, Text, StyleSheet } from '@react-pdf/renderer';
import type { ExpenseReport } from '../../../services/expenseService';
import {
  Page, FilterStrip, StatStrip, DataTable, Pill,
  pdfColors, pdfSpacing, fmtDate, fmtMoney,
  type DataTableColumn, type PdfTone, type SchoolBranding,
} from '../../../utils/pdf';

type Row = ExpenseReport['rows'][number];

interface Props {
  report: ExpenseReport;
  currency: string;
  branding: SchoolBranding;
  generatedAt: string;
}

const STATUS_TONE: Record<Row['status'], PdfTone> = { APPROVED: 'success', PENDING: 'warning', REJECTED: 'danger' };

const styles = StyleSheet.create({
  muted: { color: pdfColors.textMuted },
  title: { fontWeight: 500 },
  empty: { marginTop: pdfSpacing.md, textAlign: 'center', fontSize: 9, color: pdfColors.textMuted, fontStyle: 'italic' },
});

/**
 * The Expense Report — every expense the approval screen's filters match:
 * the letterhead, what was filtered, the totals by status, then each
 * expense with who submitted it and where it stands.
 */
const ExpenseReportDocument: React.FC<Props> = ({ report, currency, branding, generatedAt }) => {
  const money = (n: number) => fmtMoney(n, currency);
  const t = report.totals;
  const columns: Array<DataTableColumn<Row>> = [
    { key: 'n', header: '#', width: 24, align: 'center', render: (_r, i) => <Text style={styles.muted}>{i + 1}</Text> },
    { key: 'title', header: 'Title', flex: 1, render: r => <Text style={styles.title}>{r.title}</Text> },
    { key: 'amount', header: 'Amount', width: 64, align: 'right', render: r => <Text style={styles.title}>{money(r.amount)}</Text> },
    { key: 'date', header: 'Date', width: 60, render: r => r.date },
    { key: 'category', header: 'Category', width: 68, render: r => r.category ?? '—' },
    { key: 'sub', header: 'Sub-Category', width: 64, render: r => r.subCategory ?? '—' },
    { key: 'by', header: 'Submitted by', width: 70, render: r => r.submittedBy ?? '—' },
    { key: 'status', header: 'Status', width: 58, align: 'center', render: r => <Pill dense label={r.status} tone={STATUS_TONE[r.status]} /> },
  ];

  return (
    <Document title="Expense Report" author={branding.name}>
      <Page
        branding={branding}
        meta={{ title: 'Expense Report', subtitle: report.filterSummary ?? undefined, generatedAt }}
        official={{ details: [
          { label: 'Records', value: String(report.rows.length) },
          { label: 'Generated', value: fmtDate(generatedAt) },
        ] }}
      >
        {report.filterSummary && <FilterStrip summary={report.filterSummary} />}
        <StatStrip
          dense
          items={[
            { label: 'Total expenses', value: report.rows.length, tone: 'info', weight: 1.15 },
            { label: 'Total claimed', value: money(t.claimed), tone: 'primary', weight: 1.3 },
            { label: `Approved (${t.approved.count})`, value: money(t.approved.amount), tone: 'success', weight: 1.3 },
            { label: `Pending (${t.pending.count})`, value: money(t.pending.amount), tone: 'warning', weight: 1.2 },
            { label: `Rejected (${t.rejected.count})`, value: t.rejected.count, tone: 'danger' },
          ]}
        />
        {report.rows.length === 0 ? (
          <Text style={styles.empty}>No expenses match these filters.</Text>
        ) : (
          <DataTable
            columns={columns}
            rows={report.rows}
            zebra
            compact
            small
            summaryRows={[{ label: 'Total claimed (all statuses)', values: { amount: money(t.claimed) } }]}
          />
        )}
      </Page>
    </Document>
  );
};

export default ExpenseReportDocument;
